<template>
  <m-spinner
    v-if="chartDataQuery.loading.value"
    class="_spinner"
  />
  <m-table
    v-else
    :class="$attrs.class"
    :data-source="data"
    :columns="columns"
    sticky-header
  />
</template>

<script setup>
import useChartDisplay from '@/composables/chart-display';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import { chartGroupByOption, gridPageChartType } from 'shared/constants.json';
import { computed } from 'vue';
import { edgeName } from '@/composables/grid-page/chart/utils';
import { iconByType } from '@/lib/property';
import { sortAlphaNumeric } from 'shared/lib/sort';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chart: {
    type: Object,
    required: true,
  },
  chartDataQuery: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { userLang } = useLoggedInUser();

const { metricUnit, getValueText } = useChartDisplay();

const data = computed(() => {
  if (props.chartDataQuery.result.value === null) {
    return [];
  }

  if (props.chart.chartType === gridPageChartType.metric) {
    return [{
      name: metricUnit(props.chart.aggregationConfig.aggregation, props.chart.source),
      y: props.chartDataQuery.result.value,
    }];
  }

  if ([gridPageChartType.pie].includes(props.chart.chartType)) {
    return props.chartDataQuery.result.value.series[0].data;
  }

  return props.chartDataQuery.result.value.series.reduce((acc, series) => {
    const res = series.data.reduce((r, data, index) => {
      r[props.chartDataQuery.result.value.categories[index]] = data.y;
      return r;
    }, {});
    acc.push({ name: series.name === undefined ? getValueText(props.chart.aggregationConfig.aggregation) : series.name, ...res });
    return acc;
  }, []);
});

const firstColumnTitle = computed(() => {
  if (props.chart.chartType === gridPageChartType.metric) {
    return t('gridPageChartDataTable.metric');
  }
  if (props.chart.chartType === gridPageChartType.pie) {
    return edgeName(props.chart.groupBy, t, userLang.value);
  }
  if (props.chart.aggregationConfig.stackOptions.groupBy === chartGroupByOption.none) {
    return t('gridPageChartDataTable.metric');
  }
  return edgeName(props.chart.aggregationConfig.stackOptions, t, userLang.value);
});

const firstColumnIcon = computed(() => {
  if ([gridPageChartType.metric].includes(props.chart.chartType)) {
    return '';
  }

  if (props.chart.groupBy === null) {
    return '';
  }
  const groupBy = props.chart.chartType === gridPageChartType.pie ? props.chart.groupBy : props.chart.aggregationConfig.stackOptions;
  if (groupBy.groupBy === chartGroupByOption.none) {
    return '';
  }
  if (groupBy.isProperty) {
    return iconByType({ type: groupBy.property.type });
  }

  return iconByType({ type: groupBy.edgeType, edgeName: groupBy.edge });
});

const firstColumn = computed(() => ({
  key: 'name',
  dataIndex: 'name',
  title: firstColumnTitle.value,
  icon: firstColumnIcon.value,
  onFilter: (value, record) => record.name.toLowerCase().includes(value.toLowerCase()),
  sorter: sortAlphaNumeric,
  sticky: true,
}));

const columns = computed(() => {
  if (props.chartDataQuery.result.value === null || props.chartDataQuery.result.value.series?.length === 0) {
    return [];
  }

  if (props.chart.chartType === gridPageChartType.metric) {
    return [
      firstColumn.value,
      {
        key: 'value',
        dataIndex: 'y',
        title: t('gridPageChartDataTable.value'),
        sorter: sortAlphaNumeric,
      },
    ];
  }

  if (props.chart.chartType === gridPageChartType.pie) {
    return [
      firstColumn.value,
      {
        key: 'value',
        dataIndex: 'y',
        sorter: sortAlphaNumeric,
        title: getValueText(props.chart.aggregationConfig.aggregation, 'aggregator'),
      },
    ];
  }

  return [
    firstColumn.value,
    ...props.chartDataQuery.result.value.categories.map((category) => ({
      key: category,
      dataIndex: category,
      title: category,
      sorter: props.chartDataQuery.result.value.series.length > 1 ? sortAlphaNumeric : undefined,
    })),
  ];
});
</script>

<style scoped lang="scss">
._spinner {
  margin-top: 3rem;
}
</style>
